/**
 * EmotionalIntentsTab — section "Intentions émotionnelles" de Réglages.
 *
 * Tier 2 catalogue paramétrable (S2.5c). À la différence de formats/framings/
 * supports, le slug est un mot ANGLAIS sémantique envoyé à Claude S3 lors de
 * la génération de drafts. La création passe par une sous-modal de
 * validation Claude (`SlugValidationModal`) — pas de slug "deviné" du
 * label_fr via python-slugify.
 *
 * Actions par ligne via KebabMenu : Modifier, Activer/Désactiver, Supprimer.
 */

import { useState } from 'react'
import { Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import KebabMenu from '@/components/ui/KebabMenu'
import Modal from '@/components/ui/Modal'
import { FormField } from '@/components/forms/FormField'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useCreateEmotionalIntent,
  useDeleteEmotionalIntent,
  useEmotionalIntents,
  useUpdateEmotionalIntent,
} from '@/hooks/useCatalog'
import {
  tier2LabelFormSchema,
  type Tier2LabelFormParsed,
  type Tier2LabelFormValues,
} from '@/schemas/catalog'
import type { EmotionalIntentResponse } from '@/types/catalog'

import SlugValidationModal from './SlugValidationModal'

export default function EmotionalIntentsTab() {
  const { confirm } = useConfirm()
  const listQuery = useEmotionalIntents()
  const createMutation = useCreateEmotionalIntent()
  const updateMutation = useUpdateEmotionalIntent()
  const deleteMutation = useDeleteEmotionalIntent()

  const [editing, setEditing] = useState<EmotionalIntentResponse | null>(null)
  // Pour la création : 2 étapes — d'abord le label, puis la validation slug.
  const [pendingCreate, setPendingCreate] = useState<Tier2LabelFormParsed | null>(null)
  const [creatingLabel, setCreatingLabel] = useState(false)

  const handleToggleActive = (row: EmotionalIntentResponse) => {
    updateMutation.mutate(
      { id: row.id, payload: { active: !row.active } },
      {
        onSuccess: () => {
          toast.success(
            row.active ? `« ${row.label_fr} » désactivée` : `« ${row.label_fr} » activée`,
          )
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Mise à jour impossible')
        },
      },
    )
  }

  const handleDelete = async (row: EmotionalIntentResponse) => {
    const ok = await confirm({
      title: 'Supprimer cette intention émotionnelle ?',
      message: `« ${row.label_fr} » sera retirée du catalogue. Les œuvres qui la référencent conserveront le slug orphelin — préfère la désactiver.`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(row.id, {
      onError: (err) => {
        toast.error(err.detail ?? 'Suppression impossible')
      },
      onSuccess: () => {
        toast.success(`Intention « ${row.label_fr} » supprimée`)
      },
    })
  }

  const handleConfirmSlug = async (slug: string) => {
    if (!pendingCreate) return
    await new Promise<void>((resolve, reject) => {
      createMutation.mutate(
        {
          slug,
          label_fr: pendingCreate.label_fr,
          display_order: pendingCreate.display_order,
        },
        {
          onSuccess: () => {
            toast.success(`Intention « ${pendingCreate.label_fr} » ajoutée`)
            setPendingCreate(null)
            resolve()
          },
          onError: (err) => {
            toast.error(err.detail ?? 'Création impossible')
            reject(err)
          },
        },
      )
    })
  }

  const rows = listQuery.data ?? []
  const isLoading = listQuery.isLoading

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Intentions émotionnelles</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Tier 2 — ce que la personne devrait ressentir. Le slug est un mot anglais validé par
            Claude (envoyé tel quel au prompt de génération). 2-3 max sélectionnés sur une œuvre.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setCreatingLabel(true)}>
          <Plus aria-hidden="true" size={16} />
          Ajouter une intention
        </Button>
      </header>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title="Aucune intention émotionnelle"
          description="Ajoute « Apaisant », « Mélancolique », « Intriguant »… selon les ambiances que tes œuvres dégagent."
          action={
            <Button variant="primary" onClick={() => setCreatingLabel(true)}>
              Ajouter la première intention
            </Button>
          }
        />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Libellé</th>
                <th>Slug</th>
                <th>Ordre</th>
                <th>Statut</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-inactive={!row.active || undefined}>
                  <td>
                    <strong>{row.label_fr}</strong>
                  </td>
                  <td>
                    <code className="settings-slug-display">{row.slug}</code>
                  </td>
                  <td className="settings-table-numeric">{row.display_order}</td>
                  <td>
                    {row.active ? (
                      <Badge variant="success">Actif</Badge>
                    ) : (
                      <Badge variant="default">Désactivé</Badge>
                    )}
                  </td>
                  <td className="settings-table-actions">
                    <KebabMenu
                      ariaLabel={`Actions sur ${row.label_fr}`}
                      items={[
                        {
                          id: 'edit',
                          label: 'Modifier',
                          icon: Pencil,
                          onSelect: () => setEditing(row),
                        },
                        {
                          id: 'toggle',
                          label: row.active ? 'Désactiver' : 'Activer',
                          icon: row.active ? PowerOff : Power,
                          onSelect: () => handleToggleActive(row),
                        },
                        {
                          id: 'delete',
                          label: 'Supprimer',
                          icon: Trash2,
                          variant: 'danger',
                          onSelect: () => void handleDelete(row),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Étape 1 — saisie du label et display_order. */}
      {creatingLabel ? (
        <LabelModal
          mode="create"
          onClose={() => setCreatingLabel(false)}
          onSubmit={(values) => {
            setPendingCreate(values)
            setCreatingLabel(false)
          }}
        />
      ) : null}

      {/* Étape 2 — validation Claude du slug. */}
      {pendingCreate ? (
        <SlugValidationModal
          isOpen
          kind="emotional_intent"
          labelFr={pendingCreate.label_fr}
          onConfirm={handleConfirmSlug}
          onCancel={() => setPendingCreate(null)}
        />
      ) : null}

      {/* Édition d'un existant — pas de validation slug (le slug est figé). */}
      {editing ? (
        <LabelModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              updateMutation.mutate(
                {
                  id: editing.id,
                  payload: {
                    label_fr: values.label_fr,
                    display_order: values.display_order,
                  },
                },
                {
                  onSuccess: () => {
                    toast.success(`Intention « ${values.label_fr} » mise à jour`)
                    setEditing(null)
                    resolve()
                  },
                  onError: (err) => {
                    toast.error(err.detail ?? 'Mise à jour impossible')
                    reject(err)
                  },
                },
              )
            })
          }
        />
      ) : null}
    </section>
  )
}

// ─── Modal saisie label + display_order (étape 1) ──────────────────────

interface LabelModalProps {
  mode: 'create' | 'edit'
  initial?: EmotionalIntentResponse
  onClose: () => void
  onSubmit: (values: Tier2LabelFormParsed) => void | Promise<void>
}

function LabelModal({ mode, initial, onClose, onSubmit }: LabelModalProps) {
  const form = useForm<Tier2LabelFormValues, unknown, Tier2LabelFormParsed>({
    resolver: zodResolver(tier2LabelFormSchema),
    defaultValues: {
      label_fr: initial?.label_fr ?? '',
      display_order: initial?.display_order ?? 0,
    },
  })

  const submitting = form.formState.isSubmitting

  const handleValid = async (values: Tier2LabelFormParsed) => {
    await onSubmit(values)
  }

  const isCreate = mode === 'create'

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isCreate ? 'Nouvelle intention émotionnelle' : `Modifier ${initial?.label_fr}`}
      size="form"
      kicker="Catalogue · Intentions"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleValid)} noValidate>
          <Modal.Body className="settings-form-grid">
            <FormField
              name="label_fr"
              label="Libellé français"
              required
              placeholder="Mélancolique, Apaisant, Intriguant…"
              helper={
                isCreate
                  ? "Claude proposera le slug anglais sémantique à l'étape suivante."
                  : 'Le slug est figé après création (référencé par les œuvres).'
              }
              focusOnMount
            />
            {!isCreate && initial && (
              <p className="ui-text-muted ui-body-sm">
                Slug : <code className="settings-slug-display">{initial.slug}</code>
              </p>
            )}
            <FormField name="display_order" label="Ordre d'affichage" type="number" min={0} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" loading={submitting}>
              {isCreate ? 'Continuer · valider le slug' : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}
